import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Sparkles, X, ArrowRight, Rocket } from "lucide-react";

/**
 * Detects a new COCKPIT release and displays a "What's new" modal on first login
 * after publication. Marks as read when user dismisses or clicks "Découvrir".
 * Rendered once by AdminLayout.
 */
export default function CockpitWhatsNewModal({ user }) {
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!user) return;
    // Only run once per session per user (localStorage guard)
    const guardKey = `cockpit_checked_${user.id}`;
    if (sessionStorage.getItem(guardKey)) return;
    api.get("/cockpit/meta").then(({ data }) => {
      setMeta(data);
      // Trigger modal if unread >= 1 AND there is a latest release
      if (data?.unread_count > 0 && data?.latest_release) {
        setOpen(true);
      }
      sessionStorage.setItem(guardKey, "1");
    }).catch(() => {});
  }, [user]);

  const dismiss = async () => {
    setOpen(false);
    if (meta?.latest_release) {
      try { await api.post("/cockpit/mark-read", { version: meta.latest_release.version }); } catch {}
    }
  };

  const discover = async () => {
    await dismiss();
    nav("/admin/cockpit");
  };

  if (!open || !meta?.latest_release) return null;
  const r = meta.latest_release;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in" data-testid="cockpit-whats-new-modal">
      <Card className="max-w-lg w-full max-h-[90vh] overflow-auto p-7 relative bg-white" onClick={e => e.stopPropagation()}>
        <button type="button" onClick={dismiss} className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-[hsl(var(--muted))] flex items-center justify-center" data-testid="whats-new-close">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-2">
          <Sparkles className="w-3.5 h-3.5" /> Quoi de neuf ?
        </div>
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <Badge className="bg-[color:var(--brand-navy)] text-white border-0 font-mono text-xs">v{r.version}</Badge>
          <Badge className="bg-emerald-500 text-white border-0 text-[10px] uppercase tracking-wider">Nouveau</Badge>
        </div>
        <h2 className="font-display text-2xl font-bold text-[color:var(--brand-navy)] mb-2">{r.title}</h2>
        {r.summary && <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{r.summary}</p>}
        {r.features?.length > 0 && (
          <div className="rounded-lg bg-emerald-50 p-3 mb-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1.5">Nouveautés</div>
            <ul className="text-sm space-y-1 text-[color:var(--brand-navy)]">
              {r.features.slice(0, 5).map((f, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600">✓</span><span>{f}</span></li>)}
            </ul>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-2">
          <button type="button" onClick={dismiss} className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]" data-testid="whats-new-dismiss">
            Plus tard
          </button>
          <Button onClick={discover} className="bg-gradient-to-r from-[color:var(--brand-sky)] to-[color:var(--brand-navy)] text-white" data-testid="whats-new-discover">
            <Rocket className="w-4 h-4 mr-1.5" /> Découvrir
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </Card>
    </div>
  );
}
